import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider, CssBaseline, Box, CircularProgress } from "@mui/material";
import { baseTheme } from "./theme/baseTheme";
import { decodeJWT } from "./services/auth-service";
import { ROUTES } from "./config/routes";
import { LanguageProvider } from "./contexts/LanguageContext";
import LoginPage from "./pages/LoginPage";
import AuthenticatePage from "./pages/AuthenticatePage";
import DashboardPage from "./pages/DashboardPage";
import ConsentsPage from "./pages/ConsentsPage";
import PurposesPage from "./pages/PurposesPage";
import PurposeDetailPage from "./pages/PurposeDetailPage";
import ConsentRecordPage from "./pages/ConsentRecordPage";
import RequestDetailPage from "./pages/RequestDetailPage";
import DataPage from "./pages/DataPage";
import DataElementDetailPage from "./pages/DataElementDetailPage";
import RequestsPage from "./pages/RequestsPage";
import SupportPage from "./pages/SupportPage";
import NoticesPage from "./pages/NoticesPage";
import PortalLayout from "./components/layout/PortalLayout";

// Loading component
const LoadingPage = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      bgcolor: "background.default",
    }}
  >
    <Box sx={{ textAlign: "center" }}>
      <CircularProgress size={60} />
      <Box sx={{ mt: 2 }}>
        <h2>Loading...</h2>
        <p>Validating your access token</p>
      </Box>
    </Box>
  </Box>
);

// Main App Router Component
const AppRouter: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [jwt, setJwt] = useState<string | null>(null);
  const [clientData, setClientData] = useState<any>(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuthentication = () => {
      // Check if we have a JWT in localStorage
      const storedJwt = localStorage.getItem("portal_jwt");

      if (storedJwt) {
        const decoded = decodeJWT(storedJwt);
        if (decoded && decoded.exp > Math.floor(Date.now() / 1000)) {
          // JWT is still valid
          setJwt(storedJwt);
          setClientData({
            subdomain: decoded.subdomain,
            clientName: decoded.clientName,
          });
        } else {
          // JWT expired - extract clientId before removing it
          if (decoded && decoded.clientId) {
            // Extract and store clientId for OTP flow
            localStorage.setItem("portal_client_id", decoded.clientId.toString());
          }

          // Clear expired JWT
          localStorage.removeItem("portal_jwt");
        }
      }
      setLoading(false);
    };

    checkAuthentication();
  }, []);

  const handleLoginSuccess = (newJwt: string, newClientData: any) => {
    // Store JWT in localStorage (single key-value pair)
    localStorage.setItem("portal_jwt", newJwt);
    // Clean up stored clientId since we now have a valid JWT
    localStorage.removeItem("portal_client_id");
    setJwt(newJwt);
    setClientData(newClientData);
  };

  const handleLogout = () => {
    // Clear the single portal JWT from localStorage
    localStorage.removeItem("portal_jwt");
    // Also clear stored clientId
    localStorage.removeItem("portal_client_id");
    setJwt(null);
    setClientData(null);
  };

  if (loading) {
    return <LoadingPage />;
  }

  // If user is authenticated, show the main app with layout
  if (jwt && clientData) {
    return (
      <PortalLayout clientData={clientData} onLogout={handleLogout}>
        <Routes>
          <Route path={ROUTES.MANAGE_CONSENT} element={<Navigate to={ROUTES.DASHBOARD} replace />} />
          <Route path={ROUTES.DASHBOARD} element={<DashboardPage jwt={jwt} clientData={clientData} />} />
          <Route path={ROUTES.CONSENTS} element={<ConsentsPage jwt={jwt} clientData={clientData} />} />
          <Route path={ROUTES.PURPOSES} element={<PurposesPage jwt={jwt} clientData={clientData} />} />
          <Route path="/purposes/:purposeId" element={<PurposeDetailPage jwt={jwt} clientData={clientData} />} />
          <Route path="/consent/:consentHeaderId" element={<ConsentRecordPage jwt={jwt} clientData={clientData} />} />
          <Route path={ROUTES.DATA} element={<DataPage />} />
          <Route path="/data/:dataElementId" element={<DataElementDetailPage />} />
          <Route path={ROUTES.NOTICES} element={<NoticesPage clientData={clientData} />} />
          <Route path={ROUTES.REQUESTS} element={<RequestsPage />} />
          <Route path="/requests/:requestId" element={<RequestDetailPage />} />
          <Route path={ROUTES.SUPPORT} element={<SupportPage />} />
          <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
        </Routes>
      </PortalLayout>
    );
  }

  // If user is not authenticated, show login page
  // Get clientId from URL parameter, navigation state context, or localStorage
  const urlClientId = parseInt(new URLSearchParams(location.search).get("CLIENTID") || "0");
  const contextClientId = location.state?.context?.clientId || 0;
  const storedClientId = parseInt(localStorage.getItem("portal_client_id") || "0");
  const clientId = urlClientId || contextClientId || storedClientId;

  return (
    <LoginPage
      consentId={parseInt(new URLSearchParams(location.search).get("CID") || "0")}
      collectionPointId={parseInt(new URLSearchParams(location.search).get("CPID") || "0")}
      clientId={clientId}
      onLoginSuccess={handleLoginSuccess}
    />
  );
};

// Main App Component
function App() {
  const theme = baseTheme;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <Router>
          <Routes>
            <Route path={ROUTES.AUTHENTICATE} element={<AuthenticatePage />} />
            <Route path="/*" element={<AppRouter />} />
          </Routes>
        </Router>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
